import { buildSerpSnippetSegments, type SerpSnippetSegment } from './serp-snippet-segments';

export interface StructurePassageHeading {
  tag: string | null;
  title: string;
}

/** Parse “H2: Foo”, “Intro”, “Title” into a tag bubble + title. */
export function parseStructurePassageHeading(label: string | null | undefined): StructurePassageHeading {
  const raw = (label || '').trim();
  if (!raw) return { tag: null, title: '' };
  const hx = raw.match(/^(H[1-6]):\s*(.*)$/i);
  if (hx) {
    return { tag: hx[1].toUpperCase(), title: (hx[2] || '').trim() || raw };
  }
  if (/^intro$/i.test(raw)) return { tag: 'Intro', title: '' };
  if (/^title$/i.test(raw)) return { tag: 'Title', title: '' };
  if (/^meta description$/i.test(raw)) return { tag: 'Meta', title: '' };
  return { tag: null, title: raw };
}

/**
 * Keep structure evidence + query-question spans with short context; collapse the rest to “…”.
 * Makes long Intro walls scannable while locking onto delivery rhetoric and the asked question.
 */
export function buildCollapsedStructurePassageSegments(
  text: string,
  evidenceSpans: string[] | null | undefined,
  options?: {
    contextChars?: number;
    minGapForEllipsis?: number;
    questionSpans?: string[] | null;
    matchKind?: 'match' | 'closest';
  }
): SerpSnippetSegment[] {
  const raw = (text || '').replace(/\s+/g, ' ').trim();
  if (!raw) return [];

  const contextChars = options?.contextChars ?? 70;
  const minGap = options?.minGapForEllipsis ?? 40;
  const matchKind = options?.matchKind === 'closest' ? 'closest' : 'structure';
  const structureSpans = (evidenceSpans || []).map((s) => String(s || '').trim()).filter((s) => s.length >= 3);
  const questionSpans = (options?.questionSpans || [])
    .map((s) => String(s || '').trim())
    .filter((s) => s.length >= 3);
  const spans = [...structureSpans, ...questionSpans];

  if (!spans.length) {
    // No evidence: keep a short lead + ellipsis rather than the whole wall.
    if (raw.length <= 280) return [{ text: raw, emphasized: false }];
    return [
      { text: `${raw.slice(0, 220).trim()}…`, emphasized: false }
    ];
  }

  const lower = raw.toLowerCase();
  type Range = {
    start: number;
    end: number;
    coreStart: number;
    coreEnd: number;
    kind: 'structure' | 'question' | 'closest';
  };
  const cores: Range[] = [];

  const addCores = (list: string[], kind: 'structure' | 'question' | 'closest') => {
    for (const span of list.sort((a, b) => b.length - a.length)) {
      const needle = span.toLowerCase();
      let from = 0;
      while (from < lower.length) {
        const idx = lower.indexOf(needle, from);
        if (idx < 0) break;
        const end = idx + span.length;
        const overlaps = cores.some((c) => !(end <= c.coreStart || idx >= c.coreEnd));
        if (!overlaps) {
          cores.push({ start: idx, end, coreStart: idx, coreEnd: end, kind });
        }
        from = idx + Math.max(1, span.length);
      }
    }
  };

  // Questions first so they win overlapping cores (SEO “asked question” cue).
  addCores(questionSpans, 'question');
  addCores(structureSpans, matchKind);

  if (!cores.length) {
    return buildSerpSnippetSegments(raw.slice(0, 280), []);
  }

  cores.sort((a, b) => a.coreStart - b.coreStart);

  const windows: Range[] = cores.map((c) => {
    let start = Math.max(0, c.coreStart - contextChars);
    let end = Math.min(raw.length, c.coreEnd + contextChars);
    // Prefer sentence-ish boundaries when nearby.
    const before = raw.lastIndexOf('. ', c.coreStart - 1);
    if (before >= 0 && c.coreStart - before < contextChars + 40) {
      start = Math.min(start, before + 2);
    }
    const after = raw.indexOf('. ', c.coreEnd);
    if (after >= 0 && after - c.coreEnd < contextChars + 40) {
      end = Math.max(end, after + 1);
    }
    return { start, end, coreStart: c.coreStart, coreEnd: c.coreEnd, kind: c.kind };
  });

  // Merge overlapping/adjacent windows.
  const merged: Range[] = [];
  for (const w of windows) {
    const prev = merged[merged.length - 1];
    if (prev && w.start <= prev.end + minGap) {
      prev.end = Math.max(prev.end, w.end);
      prev.coreStart = Math.min(prev.coreStart, w.coreStart);
      prev.coreEnd = Math.max(prev.coreEnd, w.coreEnd);
      if (w.kind === 'question') prev.kind = 'question';
    } else {
      merged.push({ ...w });
    }
  }

  const typedHighlight = (slice: string): SerpSnippetSegment[] => {
    type Hit = { start: number; end: number; kind: 'structure' | 'question' | 'closest' };
    const hits: Hit[] = [];
    const sliceLower = slice.toLowerCase();
    const pushHits = (list: string[], kind: 'structure' | 'question' | 'closest') => {
      for (const span of list.sort((a, b) => b.length - a.length)) {
        const needle = span.toLowerCase();
        let from = 0;
        while (from < sliceLower.length) {
          const idx = sliceLower.indexOf(needle, from);
          if (idx < 0) break;
          const end = idx + span.length;
          const overlaps = hits.some((h) => !(end <= h.start || idx >= h.end));
          if (!overlaps) hits.push({ start: idx, end, kind });
          from = idx + Math.max(1, span.length);
        }
      }
    };
    pushHits(questionSpans, 'question');
    pushHits(structureSpans, matchKind);
    hits.sort((a, b) => a.start - b.start);

    if (!hits.length) return [{ text: slice, emphasized: false }];
    const out: SerpSnippetSegment[] = [];
    let cursor = 0;
    for (const h of hits) {
      if (h.start > cursor) out.push({ text: slice.slice(cursor, h.start), emphasized: false });
      out.push({
        text: slice.slice(h.start, h.end),
        emphasized: true,
        highlightKind: h.kind
      });
      cursor = h.end;
    }
    if (cursor < slice.length) out.push({ text: slice.slice(cursor), emphasized: false });
    return out.filter((s) => s.text.length > 0);
  };

  const out: SerpSnippetSegment[] = [];
  let cursor = 0;
  for (const w of merged) {
    if (w.start > cursor) {
      if (cursor === 0 && w.start > 0) {
        out.push({ text: '…', emphasized: false });
      } else if (w.start - cursor >= minGap) {
        out.push({ text: ' … ', emphasized: false });
      } else {
        // Tiny gap — keep the text.
        out.push({ text: raw.slice(cursor, w.start), emphasized: false });
      }
    }
    out.push(...typedHighlight(raw.slice(w.start, w.end)));
    cursor = w.end;
  }
  if (cursor < raw.length) {
    out.push({ text: '…', emphasized: false });
  }

  return out.length ? out : [{ text: raw.slice(0, 220) + '…', emphasized: false }];
}

function normalizeOverlapText(text: string): string {
  return (text || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function passageSpecificityRank(label: string): number {
  if (/^H[3-6]:/i.test(label)) return 3;
  if (/^H2:/i.test(label)) return 2;
  if (/^H1:/i.test(label)) return 1;
  if (/^Intro$/i.test(label)) return 0;
  return 1;
}

function structurePassagesOverlap(
  a: { text: string; evidenceSpans?: string[] },
  b: { text: string; evidenceSpans?: string[] }
): boolean {
  const aSpans = (a.evidenceSpans || []).map(normalizeOverlapText).filter((s) => s.length >= 36);
  const bSpans = (b.evidenceSpans || []).map(normalizeOverlapText).filter((s) => s.length >= 36);
  for (const x of aSpans) {
    for (const y of bSpans) {
      if (x.includes(y) || y.includes(x)) return true;
    }
  }
  const aText = normalizeOverlapText(a.text);
  const bText = normalizeOverlapText(b.text);
  for (const span of aSpans) {
    if (span.length >= 48 && bText.includes(span)) return true;
  }
  for (const span of bSpans) {
    if (span.length >= 48 && aText.includes(span)) return true;
  }
  if (!aText || !bText) return false;
  const shorter = aText.length <= bText.length ? aText : bText;
  const longer = aText.length > bText.length ? aText : bText;
  if (shorter.length < 64) return false;
  const window = Math.min(100, shorter.length);
  const probes = [
    shorter.slice(0, window),
    shorter.slice(
      Math.max(0, Math.floor((shorter.length - window) / 2)),
      Math.max(0, Math.floor((shorter.length - window) / 2)) + window
    ),
    shorter.slice(-window)
  ];
  return probes.some((sample) => sample.length >= 64 && longer.includes(sample));
}

/**
 * Drop overlapping Our-page structure cards (Intro often repeats H2/H3 how-to copy).
 * Prefer query-question headings, then higher fitPct, then H3>H2>Intro.
 */
export function dedupeOverlappingStructurePassages<
  T extends {
    label: string;
    text: string;
    evidenceSpans?: string[];
    fitPct?: number | null;
    headingIsQuestion?: boolean;
    questionSpans?: string[];
  }
>(passages: T[], max = 3): T[] {
  const hasQuestion = (p: T) =>
    Boolean(p.headingIsQuestion) || (p.questionSpans?.length || 0) > 0;

  const sorted = [...passages].sort((a, b) => {
    const aQ = hasQuestion(a);
    const bQ = hasQuestion(b);
    if (aQ !== bQ) return aQ ? -1 : 1;
    const spec = passageSpecificityRank(b.label) - passageSpecificityRank(a.label);
    if (spec !== 0) return spec;
    return (b.fitPct ?? -1) - (a.fitPct ?? -1);
  });
  const kept: T[] = [];
  for (const p of sorted) {
    const overlapIdx = kept.findIndex((k) => structurePassagesOverlap(p, k));
    if (overlapIdx >= 0) {
      const incumbent = kept[overlapIdx];
      const pQ = hasQuestion(p);
      const iQ = hasQuestion(incumbent);
      let prefer = false;
      if (pQ !== iQ) prefer = pQ;
      else {
        const specDiff =
          passageSpecificityRank(p.label) - passageSpecificityRank(incumbent.label);
        if (specDiff !== 0) prefer = specDiff > 0;
        else prefer = (p.fitPct ?? -1) > (incumbent.fitPct ?? -1);
      }
      if (prefer) kept[overlapIdx] = p;
      continue;
    }
    if (kept.length >= max) continue;
    kept.push(p);
  }
  return kept.sort((a, b) => (b.fitPct ?? -1) - (a.fitPct ?? -1));
}
